import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import themestyle from "../theme";
import AddStudentForm from "../components/AddStudentForm";
import AllStudentTable from "../components/AllStudentTable";
import ConfirmDialog from "../components/confirmdialogbox";
import JustificationDialog from "../components/JustificationDialog";
import CustomSnackbar from "../components/Snackbar";
import { DELETE_STUDENT, UPDATE_STUDENT, RESTORE_STUDENT } from "../graphql/mutation";
import { ACCOUNT_DELETION_IMPACT } from "../graphql/query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MSG } from "../utils/const";

const PAGE_TITLE = "Students - United Grooves";

type ViewType =
  | "ADD_STUDENT"
  | "ALL_STUDENTS"
  | "VIEW_STUDENT"
  | "EDIT_STUDENT";

const StudentManagement: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode?: string; id?: string }>();
  const location = useLocation();
  const [refreshCount, setRefreshCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<{
    id: string;
    name: string;
    assignedClasses?: string[];
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "info"
  >("success");
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; name: string } | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ id: string; name: string; currentStatus: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState<{
    upcomingClasses: number;
    activePasses: number;
    eventBookings: number;
    attendanceCount: number;
  } | null>(null);
  const getActiveView = (): ViewType => {
    if (location.pathname === "/students/add") return "ADD_STUDENT";
    if (mode === "view" && id) return "VIEW_STUDENT";
    if (mode === "edit" && id) return "EDIT_STUDENT";
    return "ALL_STUDENTS";
  };
  const activeView = getActiveView();
  const selectedStudentId = id;

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const [deleteStudent] = useMutation(DELETE_STUDENT);
  const [updateStudent] = useMutation(UPDATE_STUDENT);
  const [restoreStudent] = useMutation(RESTORE_STUDENT);
  const [loadImpact] = useLazyQuery(ACCOUNT_DELETION_IMPACT, { fetchPolicy: "network-only" });

  const handleView = (id: string) => {
    navigate(`/students/view/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/students/edit/${id}`);
  };

  const handleBack = () => {
    navigate("/students");
  };

  const handleSuccess = () => {
    navigate("/students");
    setRefreshCount((prev) => prev + 1);
  };

  // Activate → simple confirm (no reason). Deactivate → requires a reason (decision #12).
  const handleStatusClick = (id: string, name: string, currentStatus: boolean) => {
    setStatusTarget({ id, name, currentStatus });
    if (currentStatus) {
      setDeactivateOpen(true); // currently active → deactivating
    } else {
      setStatusConfirmOpen(true); // currently inactive → activating
    }
  };

  const handleConfirmActivate = async () => {
    if (!statusTarget) return;
    try {
      const { data: response }: any = await updateStudent({
        variables: { id: statusTarget.id, input: { isActive: true } },
      });
      if (response?.updateStudent?.success) {
        setSnackbarMessage(MSG.STATUS_UPDATED_SUCCESS);
        setSnackbarType("success");
        setRefreshCount((prev) => prev + 1);
      } else {
        setSnackbarMessage(response?.updateStudent?.message || MSG.STATUS_UPDATE_FAILED);
        setSnackbarType("error");
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage(MSG.STATUS_UPDATE_FAILED);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setStatusConfirmOpen(false);
      setStatusTarget(null);
    }
  };

  const handleConfirmDeactivate = async (reason: string) => {
    if (!statusTarget) return;
    try {
      setBusy(true);
      const { data: response }: any = await updateStudent({
        variables: { id: statusTarget.id, input: { isActive: false, justification: reason } },
      });
      if (response?.updateStudent?.success) {
        setSnackbarMessage(MSG.STATUS_UPDATED_SUCCESS);
        setSnackbarType("success");
        setRefreshCount((prev) => prev + 1);
      } else {
        setSnackbarMessage(response?.updateStudent?.message || MSG.STATUS_UPDATE_FAILED);
        setSnackbarType("error");
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage(MSG.STATUS_UPDATE_FAILED);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setBusy(false);
      setDeactivateOpen(false);
      setStatusTarget(null);
    }
  };

  const handleRestoreClick = (id: string, name: string) => {
    setRestoreTarget({ id, name });
    setRestoreOpen(true);
  };

  const handleConfirmRestore = async (reason: string) => {
    if (!restoreTarget) return;
    try {
      setBusy(true);
      const { data: response }: any = await restoreStudent({
        variables: { id: restoreTarget.id, justification: reason },
      });
      if (response?.restoreStudent?.success) {
        setSnackbarMessage(response.restoreStudent.message || "Student restored");
        setSnackbarType("success");
        setRefreshCount((prev) => prev + 1);
      } else {
        setSnackbarMessage(response?.restoreStudent?.message || "Failed to restore student");
        setSnackbarType("error");
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to restore student");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setBusy(false);
      setRestoreOpen(false);
      setRestoreTarget(null);
    }
  };

  const handleDeleteClick = async (
    id: string,
    name: string,
    assignedClasses: string[] = [],
  ) => {
    setStudentToDelete({ id, name, assignedClasses });
    setImpact(null);
    setDeleteDialogOpen(true);
    try {
      const res: any = await loadImpact({ variables: { studentId: id } });
      const data = res?.data?.accountDeletionImpact?.data;
      if (data) setImpact(data);
    } catch (err) {
      console.error("Failed to load deletion impact", err);
    }
  };

  const handleConfirmDelete = async (reason: string) => {
    if (!studentToDelete) return;
    try {
      setBusy(true);
      const { data: response }: any = await deleteStudent({
        variables: { id: studentToDelete.id, justification: reason },
      });
      if (response?.deleteStudent?.success) {
        setSnackbarMessage(response.deleteStudent.message || "Student deleted");
        setSnackbarType("success");
        setSnackbarOpen(true);
        setDeleteDialogOpen(false);
        setStudentToDelete(null);
        handleSuccess();
      } else {
        setSnackbarMessage(
          response?.deleteStudent?.message || "Failed to delete student",
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to delete student");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const isAllView = activeView === "ALL_STUDENTS";
  const isAddView = activeView === "ADD_STUDENT";

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
          }}
        >
          {(activeView === "VIEW_STUDENT" || activeView === "EDIT_STUDENT") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              Back
            </Box>
          )}
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: "24px" }}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            {activeView === "VIEW_STUDENT"
              ? "View Student"
              : activeView === "EDIT_STUDENT"
                ? "Edit Student"
                : "Students"}
          </Typography>
        </Box>

        {(activeView === "ADD_STUDENT" || activeView === "ALL_STUDENTS") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => {
                navigate("/students");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              All Students
            </Box>
            <Box
              onClick={() => {
                navigate("/students/add");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAddView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAddView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Add Student
            </Box>
          </Box>
        )}
      </Box>

      {activeView === "ADD_STUDENT" && (
        <AddStudentForm
          onSuccess={handleSuccess}
          onCancel={() => navigate("/students")}
          onDelete={handleDeleteClick}
        />
      )}

      {activeView === "ALL_STUDENTS" && (
        <AllStudentTable
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onStatusToggle={handleStatusClick}
          onRestore={handleRestoreClick}
          refreshTrigger={refreshCount}
        />
      )}

      {activeView === "VIEW_STUDENT" && selectedStudentId && (
        <AddStudentForm
          mode="view"
          id={selectedStudentId}
          onCancel={handleBack}
          onEdit={() => navigate(`/students/edit/${selectedStudentId}`)}
          onDelete={handleDeleteClick}
        />
      )}

      {activeView === "EDIT_STUDENT" && selectedStudentId && (
        <AddStudentForm
          mode="edit"
          id={selectedStudentId}
          onSuccess={handleSuccess}
          onCancel={handleBack}
          onDelete={handleDeleteClick}
        />
      )}

      <JustificationDialog
        open={deleteDialogOpen}
        title="Delete student account?"
        message={
          `Remove ${studentToDelete?.name || "this student"}? Any remaining booked classes ` +
          `will be cancelled with no refund and the account will be archived. Their data is ` +
          `retained per the data-protection policy and can be restored until it is purged.`
        }
        extra={
          impact ? (
            <Box
              sx={{
                p: 1.5,
                bgcolor: "#FFF8F8",
                border: "1px solid #FDA29B",
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 0.5 }}>
                Account impact
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#667085" }}>
                Upcoming classes: {impact.upcomingClasses} · Active passes: {impact.activePasses} ·
                Event bookings: {impact.eventBookings} · Attendance records: {impact.attendanceCount}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "#98A2B3" }}>
              Checking account impact…
            </Typography>
          )
        }
        requireReason
        confirmText="Delete"
        confirmColor="error"
        busy={busy}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setDeleteDialogOpen(false);
          setStudentToDelete(null);
        }}
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />

      {/* Activate → simple confirm (no reason needed). */}
      <ConfirmDialog
        open={statusConfirmOpen}
        icon={<CheckCircleIcon sx={{ fontSize: 50, color: "#A3D451" }} />}
        message={
          statusTarget ? `${MSG.CONFIRM_ACTION_PREFIX} activate ${statusTarget.name}?` : ""
        }
        onConfirm={handleConfirmActivate}
        onClose={() => {
          setStatusConfirmOpen(false);
          setStatusTarget(null);
        }}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonColor="#A3D451"
      />

      {/* Deactivate → requires a reason (decision #12). */}
      <JustificationDialog
        open={deactivateOpen}
        title={`Deactivate ${statusTarget?.name || "student"}?`}
        message="The student will be set to inactive and unable to log in. This is saved to the audit log."
        requireReason
        confirmText="Deactivate"
        confirmColor="error"
        busy={busy}
        onConfirm={handleConfirmDeactivate}
        onClose={() => {
          setDeactivateOpen(false);
          setStatusTarget(null);
        }}
      />

      {/* Restore → requires a reason (decision #1/#12). */}
      <JustificationDialog
        open={restoreOpen}
        title={`Restore ${restoreTarget?.name || "student"}?`}
        message="The account will be reactivated and the student will be able to log in again. This is saved to the audit log."
        requireReason
        confirmText="Restore"
        confirmColor="success"
        busy={busy}
        onConfirm={handleConfirmRestore}
        onClose={() => {
          setRestoreOpen(false);
          setRestoreTarget(null);
        }}
      />
    </Box>
  );
};

export default StudentManagement;
